import { ArrowRight } from "react-bootstrap-icons";
import { useMemo } from "react";
import { useNavigate } from "react-router";
import Footer from "../../components/Footer/Footer";
import catlogicLogo from "../../assets/logo_white.svg";
import SiteNav from "../../components/SiteNav/SiteNav";
import Seo, { SITE_URL } from "../../components/Seo/Seo";
import { ABOUT_COPY } from "../../content/localizedPageCopy";
import { useLocale } from "../../contexts/LocaleContext";
import { navigateToHomeSection } from "../../utils/homeSectionNavigation";
import { createHeroParticles } from "../../utils/heroParticles";
import styles from "./About.module.scss";

export default function AboutView() {
  const nav = useNavigate();
  const { locale } = useLocale();
  const copy = ABOUT_COPY[locale];
  const heroParticles = useMemo(() => createHeroParticles(), []);

  const structuredData = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: copy.pageTitle,
      description: copy.metaDescription,
      url: `${SITE_URL}/about`,
      inLanguage: locale,
      mainEntity: {
        "@type": "Organization",
        name: "CatLogic",
        url: SITE_URL,
        foundingDate: "2024",
        founder: {
          "@type": "Person",
          name: "Sergey Sharkov",
        },
      },
    }),
    [copy, locale]
  );

  return (
    <main className={styles.page}>
      <Seo
        title={copy.pageTitle}
        description={copy.metaDescription}
        keywords={copy.metaKeywords}
        path="/about"
        structuredData={structuredData}
      />

      <SiteNav solidThreshold={320} />

      <section className={styles.hero}>
        <div className={styles.heroParticles} aria-hidden="true">
          {heroParticles.map((particle, index) => (
            <span
              key={index}
              className={styles.particle}
              style={
                {
                  width: particle.size,
                  height: particle.size,
                  top: particle.top,
                  left: particle.left,
                  background: `radial-gradient(circle, ${particle.color}, transparent 70%)`,
                  animationDuration: particle.duration,
                  animationDelay: particle.delay,
                  "--particle-scale-mid": particle.scaleMid,
                  "--particle-scale-low": particle.scaleLow,
                } as React.CSSProperties
              }
            />
          ))}
        </div>

        <div className={styles.container}>
          <div className={styles.heroCenter}>
            <img className={styles.heroLogo} src={catlogicLogo} alt="CatLogic" />
            <span className={styles.heroCaption}>{copy.badge}</span>

            <h1 className={styles.heroTitle}>{copy.heroTitle}</h1>
            <p className={styles.heroDescription}>{copy.heroBody}</p>
          </div>

          <div className={styles.heroMetrics}>
            {copy.highlights.map((item) => (
              <div key={item.label} className={styles.metric}>
                <strong className={styles.metricValue}>{item.value}</strong>
                <span>{item.label}</span>
                <p>{item.body}</p>
              </div>
            ))}
          </div>

          <div className={styles.heroProcess}>
            <div className={styles.processIntro}>
              <span className={styles.panelTag}>{copy.panelLabel}</span>
              <h2>{copy.panelTitle}</h2>
              <p>{copy.panelBody}</p>
            </div>

            <div className={styles.processSteps}>
              {copy.focusPoints.map((step, index) => (
                <div key={step} className={styles.processStep}>
                  <span className={styles.processIndex}>{`0${index + 1}`}</span>
                  <p>{step}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className={styles.surface}>
        <section className={styles.lightBand}>
          <div className={styles.container}>
            <div className={styles.sectionHeading}>
              <span className={styles.kicker}>{copy.storyKicker}</span>
              <h2>{copy.storyTitle}</h2>
            </div>

            <div className={styles.grid}>
              {copy.storyCards.map((item) => (
                <article key={item.title} className={styles.card}>
                  <span className={styles.cardEyebrow}>{item.eyebrow}</span>
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.darkBand}>
          <div className={styles.container}>
            <div className={styles.sectionIntro}>
              <div>
                <span className={styles.kicker}>{copy.positionKicker}</span>
                <h2>{copy.positionTitle}</h2>
              </div>
              <p>{copy.positionBody}</p>
            </div>

            <div className={styles.principlesGrid}>
              {copy.principles.map((item) => (
                <article key={item.title} className={styles.principleCard}>
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.lightBand}>
          <div className={styles.container}>
            <div className={styles.ctaPanel}>
              <span className={styles.kicker}>{copy.nextKicker}</span>
              <h2>{copy.ctaTitle}</h2>
              <p>{copy.ctaBody}</p>

              <div className={styles.ctaActions}>
                <button type="button" className={styles.primaryButton} onClick={() => nav("/career")}>
                  {copy.careersCta} <ArrowRight />
                </button>
                <button type="button" className={styles.secondaryButton} onClick={() => navigateToHomeSection(nav, "contacts")}>
                  {copy.contactCta} <ArrowRight />
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </main>
  );
}
